import React from 'react';
import { Show } from 'Layout';
import { SurveysAPI } from '../../API';
import { isPanelCountry, CHILD_OVERRIDABLE_FIELDS } from './surveyConstants';
import { childProjectNameParts, deriveChildProjectSuffix, composeChildProjectName } from './childProjectName';
import { checkNumberValidation } from 'Helper/Validations';
import csvtojson from "csvtojson";
import * as XLSX from "xlsx";

type PoolStats = {
  stats: {
    available: number;
    claimed: number;
    consumed: number;
    total: number;
  };
  testStats: {
    available: number;
    claimed: number;
    consumed: number;
    total: number;
  };
};

type UniqueLinksUploadResult = {
  total_in_csv: number;
  added: number;
  skipped_duplicates: number;
  test_total_in_csv?: number;
  test_invalid?: number;
  error?: string;
};

type Props = {
  childState: any;
  parentState: any;
  countryName: string;
  shortName: string;
  // Unique per-CHILD-instance key (Form.tsx childStateKey: child.id for saved,
  // `new:<sn>[:nonce]` for unsaved). Used for ALL DOM id/htmlFor/radio-name
  // attributes so same-country duplicate tabs ("JP" + "JP 2") never collide:
  // a shared id/htmlFor makes a <label> click hit the FIRST matching (sibling)
  // element, and a shared radio `name` merges both tabs into one radio group —
  // either way the change routes to the wrong child. shortName stays for display
  // text only. Falls back to shortName if a caller omits it.
  instanceKey?: string;
  // Saved child UUID — present only after the child has been persisted. Drives
  // both the pool-stats fetch (no point asking the backend about an unsaved
  // child) and the upload-result lookup. Undefined for `new:<sn>:<nonce>` tabs.
  childId?: string;
  // Per-child upload outcome from the most recent Save response. Surfaced as
  // inline green feedback under the CSV input. Cleared/overwritten by Form.tsx
  // on each Save, so a stale value never lingers across edits.
  uniqueLinksUploadResult?: UniqueLinksUploadResult;
  onChange: (field: string, value: any) => void;
  isEditMode: boolean;
  preScreenSection: React.ReactNode;
  // Same source as PartnersTab's isParentSurvey (Form.tsx: !this.state.data.parentid).
  // True when Form.tsx is editing the parent project (the normal multi-country-tabs
  // session) — the default, so every existing caller keeps today's behavior:
  // the Survey Status radio keeps rendering here exactly as before.
  // False only in a child-direct-edit session (Form.tsx opened directly on a
  // child's own id, so this is the single remaining country tab). There, the
  // Partners tab's own "Survey Status*" control (dashboardpartners.tsx:619+) is
  // now also visible in the same session — a second, differently-scoped field
  // (the attached partner's status, not the survey's own) sharing the same
  // label. To avoid that visual duplication, this country-tab copy is hidden
  // in child-direct-edit mode; the child's own surveyType stays settable from
  // the survey list's per-row status control instead.
  isParentSurvey?: boolean;
  role?: string;
  // Which fields to render. 'market': everything except the panel launch
  // settings (the detail page's Markets tab). 'launch': only the panel launch
  // settings (its Schedule tab). Omitted: all fields, the launch settings under
  // a "Panel launch settings" sub-heading (the legacy survey modals).
  fieldGroup?: 'market' | 'launch';
  // Layout only: with fieldGroup 'market', a panel market shows this where the
  // launch settings would be (the create page's pointer to the Schedule tab).
  launchNote?: React.ReactNode;
  // Layout only: no Survey Status radios. The survey detail page changes status
  // through the header and Overview dropdowns; the create page sets it once in
  // step 1.
  hideStatus?: boolean;
};

type State = {
  poolStats: PoolStats | null;
};

// Shared style for the pool-stats table cells (border + small padding). Kept
// inline so the table doesn't depend on the global stylesheet for layout.
const poolCellStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  padding: '4px 10px',
  textAlign: 'center',
};

/**
 * ChildSurveyTab renders per-country child fields.
 * Panel countries (IN, MY, SG, UK, PH, JP) get the full field set.
 * Non-panel countries get only URLs + Unique Links + Pre-screening.
 *
 * When a field is changed from its inherited parent value, the field name
 * is tracked in `overriddenFields` so parent updates skip it during cascade.
 */
export class ChildSurveyTab extends React.Component<Props, State> {
  state: State = { poolStats: null };

  // Track in-flight requests so an unmount or a rapid id-change doesn't write
  // a stale poolStats back onto the next-child's view.
  private _statsRequestId = 0;
  private _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
    this.fetchPoolStatsIfNeeded();
  }

  componentDidUpdate(prevProps: Props) {
    // Refetch when the saved-child id flips into existence (first save) or
    // changes (parent swapped to a different child). Also refetch when a new
    // upload-result arrives — the pool just changed and the table is stale.
    const idChanged = prevProps.childId !== this.props.childId;
    const uploadResultChanged = prevProps.uniqueLinksUploadResult !== this.props.uniqueLinksUploadResult
      && !!this.props.uniqueLinksUploadResult;
    if (idChanged || uploadResultChanged) {
      this.fetchPoolStatsIfNeeded();
    }
    // Stale-Count Bug fix: clear the pending file and links array upon a successful save,
    // so the UI doesn't sum the now-saved links with the new backend pool stats.
    if (uploadResultChanged && !this.props.uniqueLinksUploadResult.error) {
      this.handleUniqueLinksReset();
      this.handleTestUniqueLinksReset();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchPoolStatsIfNeeded = () => {
    const { childId } = this.props;
    if (!childId) {
      this.setState({ poolStats: null });
      return;
    }
    const requestId = ++this._statsRequestId;
    SurveysAPI.getUniqueLinksStats(childId)
      .then((data: PoolStats | null) => {
        if (!this._isMounted) return;
        // Discard stale responses — only the most recent request wins.
        if (requestId !== this._statsRequestId) return;
        this.setState({ poolStats: data });
      })
      .catch(() => {
        // Non-fatal — leave the previous snapshot in place so a transient
        // network error doesn't wipe the view.
        if (requestId !== this._statsRequestId) return;
        this.setState({ poolStats: null });
      });
  };

  handleFieldChange = (field: string, value: any) => {
    this.props.onChange(field, value);
  };

  // Unique Links CSV import. Phase 1: client-side parse + per-row URL validation.
  // The parsed `uniqueLinks` array is staged on childState only — it is NOT sent
  // to the backend in this phase (Form.tsx payload builder is untouched).
  parseUniqueLinksCsv = (file: File): Promise<{ urls: string[]; errors: string[] }> => {
    return new Promise((resolve) => {
      const isExcel = file.name.match(/\.xlsx?$/i);
      const reader = new FileReader();

      const processCandidates = (candidates: string[]) => {
        candidates = candidates
          .map(c => c.replace(/[,;\s]+$/, ''))
          .filter(c => c.length > 0);
        const valid: string[] = [];
        const errors: string[] = [];
        candidates.forEach((u, i) => {
          if (!/^https?:\/\//i.test(u)) {
            errors.push(`Row ${i + 1}: not a valid URL (${u.slice(0, 60)})`);
          } else {
            valid.push(u);
          }
        });
        resolve({ urls: valid, errors });
      };

      if (isExcel) {
        reader.onload = () => {
          try {
            const data = reader.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const parsed: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            let candidates: string[] = [];
            if (parsed.length > 0) {
              const firstRow = parsed[0];
              const isUrlHeader = String(firstRow[0] || '').toLowerCase() === 'url';
              if (isUrlHeader) {
                candidates = parsed.slice(1).map(r => String(r[0] || '').trim());
              } else {
                candidates = parsed.map(r => String(r[0] || '').trim());
              }
            }
            processCandidates(candidates);
          } catch (e: any) {
            resolve({ urls: [], errors: [`Parse error: ${e?.message || 'unknown'}`] });
          }
        };
        reader.onerror = () => resolve({ urls: [], errors: [reader.error?.message || 'Read failed.'] });
        reader.readAsBinaryString(file);
      } else {
        reader.onload = async () => {
          try {
            const content = (reader.result as string) || '';
            if (!content.trim()) { resolve({ urls: [], errors: ['File is empty.'] }); return; }
            let candidates: string[] = [];
            const parsed = await csvtojson({ trim: true, flatKeys: true }).fromString(content);
            if (parsed.length > 0) {
              const keys = Object.keys(parsed[0]);
              const urlKey = keys.find(k => k.toLowerCase() === 'url');
              if (urlKey) {
                candidates = parsed.map((r: any) => String(r[urlKey] || '').trim());
              } else if (keys.length === 1) {
                // Single-column with arbitrary header — treat header as a row too.
                const onlyKey = keys[0];
                candidates = [onlyKey, ...parsed.map((r: any) => String(r[onlyKey] || '').trim())];
              }
            }
            // Fallback: line-by-line if csvtojson produced nothing useful.
            if (candidates.length === 0) {
              candidates = content.split(/\r?\n/).map(l => l.trim());
            }
            processCandidates(candidates);
          } catch (e: any) {
            resolve({ urls: [], errors: [`Parse error: ${e?.message || 'unknown'}`] });
          }
        };
        reader.onerror = () => resolve({ urls: [], errors: [reader.error?.message || 'Read failed.'] });
        reader.readAsText(file);
      }
    });
  };

  handleUniqueLinksFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const { urls, errors } = await this.parseUniqueLinksCsv(file);
    this.handleFieldChange('uniqueLinksFile', file);
    this.handleFieldChange('uniqueLinks', urls);
    this.handleFieldChange('uniqueLinksErrors', errors);
  };

  handleUniqueLinksReset = () => {
    this.handleFieldChange('uniqueLinksFile', null);
    this.handleFieldChange('uniqueLinks', []);
    this.handleFieldChange('uniqueLinksErrors', []);
  };

  handleTestUniqueLinksFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const { urls, errors } = await this.parseUniqueLinksCsv(file);
    this.handleFieldChange('testUniqueLinksFile', file);
    this.handleFieldChange('testUniqueLinks', urls);
    this.handleFieldChange('testUniqueLinksErrors', errors);
  };

  handleTestUniqueLinksReset = () => {
    this.handleFieldChange('testUniqueLinksFile', null);
    this.handleFieldChange('testUniqueLinks', []);
    this.handleFieldChange('testUniqueLinksErrors', []);
  };

  setNumberField = (e: any, field: string) => {
    if (!checkNumberValidation(e) && e.target.value >= 0) {
      this.handleFieldChange(field, e.target.value);
    }
  };

  handleSendSurvey = (e: any) => {
    const { name, checked } = e.target;
    this.handleFieldChange(name, checked ? 'Y' : 'N');
  };

  isOverridden = (field: string): boolean => {
    return (this.props.childState.overriddenFields || []).includes(field);
  };

  fieldLabel = (label: string, _field: string): React.ReactNode => {
    return label;
  };

  isChecked = (name: string): boolean => this.props.childState[name] === 'Y';

  // Panel launch settings: SMS/WhatsApp Name, Point Allocation, DX Quota, Send
  // Survey, points and Disclaimer. Panel markets only.
  renderLaunchFields(idKey: string) {
    const { childState } = this.props;
    return (
      <>
          {/* SMS/WhatsApp Name */}
          <div className="row mt-2">
            <div className="col">
              <label>{this.fieldLabel('SMS/WhatsApp Name*', 'name')}</label>
              <input
                className="form-control"
                name="name"
                maxLength={30}
                onChange={(e) => this.handleFieldChange('name', e.target.value)}
                value={childState.name || ''}
                placeholder="Max 30 characters"
              />
              <small className="text-muted">
                {(childState.name || '').length}/30
              </small>
              {/* WhatsApp template variable preview */}
              <div className="mt-1 p-2 bg-light rounded" style={{ fontSize: '0.85rem' }}>
                <strong>WhatsApp preview:</strong>{' '}
                Hi {'{{userName}}'}, we have a new survey <strong>{childState.name || '...'}</strong> for you. Click the link to participate.
              </div>
            </div>
          </div>

          {/* Point Allocation */}
          <div className="row mt-2">
            <div className="col mt-2">
              <label>{this.fieldLabel('Point Allocation*', 'pointAllocationType')}</label><br />
              <div className="custom-control custom-radio custom-control-inline">
                <input type="radio" id={`pointAlloc-manual-${idKey}`} name={`pointAlloc-${idKey}`}
                  checked={childState.pointAllocationType === 'Manual'} className="custom-control-input"
                  onChange={() => this.handleFieldChange('pointAllocationType', 'Manual')} />
                <label className="custom-control-label" htmlFor={`pointAlloc-manual-${idKey}`}>Manual</label>
              </div>
              <div className="custom-control custom-radio custom-control-inline">
                <input type="radio" id={`pointAlloc-auto-${idKey}`} name={`pointAlloc-${idKey}`}
                  checked={childState.pointAllocationType === 'Auto'} className="custom-control-input"
                  onChange={() => this.handleFieldChange('pointAllocationType', 'Auto')} />
                <label className="custom-control-label" htmlFor={`pointAlloc-auto-${idKey}`}>Auto</label>
              </div>
            </div>
          </div>

          {/* DX Quota & Send Survey */}
          <div className="row mt-2">
            <div className="col mt-2">
              <label>{this.fieldLabel('DX Quota', 'IPollsQuotaLimit')}</label>
              <div className="col">
                <input
                  type="checkbox"
                  id={`dxQuota-${idKey}`}
                  name="IPollsQuotaLimit"
                  onChange={(e) => this.handleFieldChange('IPollsQuotaLimit', e.target.checked ? 'Y' : 'N')}
                  checked={childState.IPollsQuotaLimit === 'Y'}
                />
              </div>
            </div>
            <div className="col mt-2">
              <label>{this.fieldLabel('Send Survey*', 'email')}</label><br />
              <div className="custom-control custom-control-inline">
                <input type="checkbox" id={`email-${idKey}`} name="email"
                  className="custom-control-input" checked={this.isChecked('email')}
                  onChange={this.handleSendSurvey} />
                <label className="custom-control-label" htmlFor={`email-${idKey}`}>Email</label>
              </div>
              <div className="custom-control custom-control-inline">
                <input type="checkbox" id={`sms-${idKey}`} name="sms"
                  className="custom-control-input" checked={this.isChecked('sms')}
                  onChange={this.handleSendSurvey} />
                <label className="custom-control-label" htmlFor={`sms-${idKey}`}>SMS</label>
              </div>
              <div className="custom-control custom-control-inline">
                <input type="checkbox" id={`whatsapp-${idKey}`} name="whatsapp"
                  className="custom-control-input" checked={this.isChecked('whatsapp')}
                  onChange={this.handleSendSurvey} />
                <label className="custom-control-label" htmlFor={`whatsapp-${idKey}`}>WhatsApp</label>
              </div>
            </div>
          </div>

          {/* DX Quota (Client Quota is parent-only — not shown here) */}
          <div className="row mt-2">
            <div className="col-6">
              <label>{this.fieldLabel('DX Quota', 'userLimitCutoff')}</label>
              <input
                type="text"
                className="form-control"
                id={`userLimitCutoff-${idKey}`}
                name="userLimitCutoff"
                onChange={(e) => this.setNumberField(e, 'userLimitCutoff')}
                value={childState.userLimitCutoff ?? 0}
                placeholder="Enter here"
                min={0}
                disabled={childState.IPollsQuotaLimit !== 'Y'}
              />
            </div>
          </div>

          {/* Points */}
          <div className="row mt-2">
            <div className="col">
              <label>{this.fieldLabel('Complete Points*', 'ceggPoints')}</label>
              <input type="text" className="form-control" name="ceggPoints"
                onChange={(e) => this.setNumberField(e, 'ceggPoints')}
                value={childState.ceggPoints ?? 0} placeholder="Enter here" min={0} />
            </div>
            <div className="col">
              <label>{this.fieldLabel('Terminate Points*', 'terminate')}</label>
              <input type="text" className="form-control" name="terminate"
                onChange={(e) => this.setNumberField(e, 'terminate')}
                value={childState.terminate ?? 0} placeholder="Enter here" min={0} />
            </div>
          </div>

          <div className="row mt-2">
            <div className="col">
              <label>{this.fieldLabel('Over Quota Points*', 'overquota')}</label>
              <input type="text" className="form-control" name="overquota"
                onChange={(e) => this.setNumberField(e, 'overquota')}
                value={childState.overquota ?? 0} placeholder="Enter here" min={0} />
            </div>
            <div className="col" />
          </div>

          {/* Disclaimer */}
          <div className="row mt-2">
            <div className="col">
              <label>{this.fieldLabel('Disclaimer', 'disclaimer')}</label>
              <textarea
                className="form-control"
                style={{ height: '120px' }}
                onChange={(e) => this.handleFieldChange('disclaimer', e.target.value)}
                value={childState.disclaimer || ''}
                placeholder="Please enter"
              />
            </div>
          </div>
      </>
    );
  }

  render() {
    const { childState, shortName, instanceKey, preScreenSection, fieldGroup } = this.props;
    const isPanel = isPanelCountry(shortName);
    // DOM-id scope: unique per child instance so same-country duplicate tabs
    // never share an id/htmlFor/radio-name (see Props.instanceKey). shortName
    // remains for human-readable display text only.
    const idKey = instanceKey || shortName;
    const showMarket = fieldGroup !== 'launch';
    const showLaunch = fieldGroup !== 'market';

    if (!showMarket) {
      return <div>{isPanel && this.renderLaunchFields(idKey)}</div>;
    }

    return (
      <div>
        {/* Child Project Name — free-form, editable. Backed by
            childStates[childKey].childProjectName (own DB column). Cascades to the
            parent Project Setup tab via the shared state path. Replaces the old
            read-only computed "Child Project ID" display. */}
        <div className="row mt-2">
          {this.props.isEditMode && (
            <div className="col-6">
              <label>Child Project Name</label>
              {(() => {
                // Locked prefix = `${parentProjectName}_${countryCode}_`. PM edits only
                // the suffix; combined `${prefix}${suffix}` (or bare base when suffix is
                // empty) is written back to childStates[childKey].childProjectName so the
                // sibling Project Setup row stays in sync via the same shared state path.
                // Split/compose live in childProjectName.ts — shared with
                // ParentProjectTab.tsx so the two inputs behave identically.
                // The suffix is never trimmed on the read path (spaces the PM types
                // must survive to the next render); whitespace is normalized once,
                // at submit, in Form.formValues().
                const parentName = (this.props.parentState && (this.props.parentState.parentProjectName || this.props.parentState.projectName)) || '';
                const countryCode = shortName || '';
                const { prefix: lockedPrefix } = childProjectNameParts(parentName, countryCode);
                const current = childState.childProjectName || '';
                const suffix = deriveChildProjectSuffix(current, parentName, countryCode);
                const handleSuffixChange = (e: any) => {
                  const newFull = composeChildProjectName(parentName, countryCode, e.target.value);
                  this.handleFieldChange('childProjectName', newFull);
                };
                return (
                  <div className="input-group">
                    <span className="input-group-text" style={{ userSelect: 'none' }}>
                      {lockedPrefix}
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      name="childProjectName"
                      value={suffix}
                      onChange={handleSuffixChange}
                      placeholder="optional suffix"
                    />
                  </div>
                );
              })()}
            </div>
          )}
          <div className="col-6">
            <label>Country</label>
            <input
              className="form-control"
              value={`${this.props.countryName} (${shortName})`}
              disabled
              readOnly
            />
          </div>
        </div>

        {/* Panel country: full field set */}
        <Show when={isPanel}>
          {/* Survey Status (Point Allocation is in the launch settings) */}
          <div className="row mt-2">
            {/* Survey Status* — hidden in child-direct-edit mode; see the
                isParentSurvey prop doc above for why. Also hidden by hideStatus. */}
            {this.props.isParentSurvey !== false && !this.props.hideStatus && (
            <div className="col mt-2">
              <label>{this.fieldLabel('Survey Status*', 'surveyType')}</label><br />
              <div className="custom-control custom-radio custom-control-inline">
                <input type="radio" id={`surveyType-test-${idKey}`} name={`surveyType-${idKey}`}
                  checked={childState.surveyType === 'Test'} className="custom-control-input"
                  onChange={() => this.handleFieldChange('surveyType', 'Test')} />
                <label className="custom-control-label" htmlFor={`surveyType-test-${idKey}`}>Test</label>
              </div>
              <div className="custom-control custom-radio custom-control-inline">
                <input type="radio" id={`surveyType-open-${idKey}`} name={`surveyType-${idKey}`}
                  checked={childState.surveyType === 'Open'} className="custom-control-input"
                  onChange={() => this.handleFieldChange('surveyType', 'Open')} />
                <label className="custom-control-label" htmlFor={`surveyType-open-${idKey}`}>Live</label>
              </div>
              <div className="custom-control custom-radio custom-control-inline">
                <input type="radio" id={`surveyType-hold-${idKey}`} name={`surveyType-${idKey}`}
                  checked={childState.surveyType === 'Hold'} className="custom-control-input"
                  onChange={() => this.handleFieldChange('surveyType', 'Hold')} />
                <label className="custom-control-label" htmlFor={`surveyType-hold-${idKey}`}>On Hold</label>
              </div>
              <div className="custom-control custom-radio custom-control-inline">
                <input type="radio" id={`surveyType-wait-${idKey}`} name={`surveyType-${idKey}`}
                  checked={childState.surveyType === 'Wait'} className="custom-control-input"
                  onChange={() => this.handleFieldChange('surveyType', 'Wait')} />
                <label className="custom-control-label" htmlFor={`surveyType-wait-${idKey}`}>Ids awaited</label>
              </div>
              <div className="custom-control custom-radio custom-control-inline">
                <input type="radio" id={`surveyType-close-${idKey}`} name={`surveyType-${idKey}`}
                  checked={childState.surveyType === 'Close'} className="custom-control-input"
                  onChange={() => this.handleFieldChange('surveyType', 'Close')} />
                <label className="custom-control-label" htmlFor={`surveyType-close-${idKey}`}>End</label>
              </div>
              {(this.props.role === 'admin' || this.props.role === 'super_admin') && (
                <div className="custom-control custom-radio custom-control-inline">
                  <input type="radio" id={`surveyType-error-${idKey}`} name={`surveyType-${idKey}`}
                    checked={childState.surveyType === 'Error'} className="custom-control-input"
                    onChange={() => this.handleFieldChange('surveyType', 'Error')} />
                  <label className="custom-control-label" htmlFor={`surveyType-error-${idKey}`}>Error</label>
                </div>
              )}
            </div>
            )}
          </div>

          <div className="row ml-3 mt-3">
              <div className="col">
                <input
                  type="checkbox"
                  id={`verisoul-${idKey}`}
                  name="verisoul"
                  className="custom-control-input"
                  checked={!!childState.verisoul}
                  onChange={() => {
                    this.handleFieldChange('verisoul', !childState.verisoul);
                    if (!childState.verisoul) {
                      // Init default to 60 when turned on
                      if (!childState.verisoul_risk_threshold) {
                        this.handleFieldChange('verisoul_risk_threshold', 70);
                      }
                    }
                  }}
                />
                <label className="custom-control-label" htmlFor={`verisoul-${idKey}`}>
                  {this.fieldLabel('Verisoul', 'verisoul')}
                </label>
                
                {!!childState.verisoul && (
                  <div className="mt-2 pl-3 border-left border-primary">
                    <label htmlFor={`verisoulRiskThresholdInput-${idKey}`} className="small font-weight-bold text-dark mb-1">
                      Verisoul Risk Threshold Score (%)
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm w-50"
                      id={`verisoulRiskThresholdInput-${idKey}`}
                      min={0}
                      max={100}
                      value={childState.verisoul_risk_threshold ?? 70}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        this.handleFieldChange('verisoul_risk_threshold', isNaN(val) ? 0 : val);
                      }}
                    />
                    <small className="text-muted d-block mt-1">
                      Requests with a Verisoul risk score &ge; this percentage will be automatically blocked (default: 60%).
                    </small>
                  </div>
                )}
              </div>
          </div>
          {showLaunch ? (
            <>
              <h6 className="mt-4 pt-3 border-top">Panel launch settings</h6>
              {this.renderLaunchFields(idKey)}
            </>
          ) : (this.props.launchNote ? (
            <p className="text-muted small mt-3 mb-0">{this.props.launchNote}</p>
          ) : null)}
        </Show>

        {/* Both panel and non-panel: URLs + Unique Links */}
        <div className="row ml-3 mt-3">
          <div className="col">
            <input
              type="checkbox"
              id={`useUniqueLinks-${idKey}`}
              name="useUniqueLinks"
              className="custom-control-input"
              checked={!!childState.useUniqueLinks}
              onChange={() => this.handleFieldChange('useUniqueLinks', !childState.useUniqueLinks)}
            />
            <label className="custom-control-label" htmlFor={`useUniqueLinks-${idKey}`}>
              {this.fieldLabel('Use Unique Links', 'useUniqueLinks')}
            </label>
          </div>
        </div>

        <Show when={!childState.useUniqueLinks}>
          <div className="row mt-2">
            <div className="col">
              <label>{this.fieldLabel('Survey Test URL', 'testurl')}</label>
              <input
                className="form-control"
                name="testurl"
                onChange={(e) => this.handleFieldChange('testurl', e.target.value)}
                value={childState.testurl || ''}
                placeholder="Enter here"
              />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col">
              <label>{this.fieldLabel('Survey Live URL', 'url')}</label>
              <input
                className="form-control"
                name="url"
                onChange={(e) => this.handleFieldChange('url', e.target.value)}
                value={childState.url || ''}
                placeholder="Enter here"
              />
            </div>
          </div>
        </Show>

        <Show when={!!childState.useUniqueLinks}>
          <>
            <div className="row mt-2">
            <div className="col">
              <label>{this.fieldLabel('Live Unique Links CSV*', 'uniqueLinks')}</label>
              {!childState.uniqueLinksFile ? (
                <div>
                  <input
                    type="file"
                    accept=".csv,text/csv,.xlsx,.xls"
                    className="form-control-file"
                    onChange={this.handleUniqueLinksFile}
                  />
                  <small className="d-block text-muted mt-1">
                    Single column CSV or Excel of URLs. Header `url` optional. Each URL must start with http:// or https://.
                  </small>
                </div>
              ) : (
                <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <strong>{childState.uniqueLinksFile.name}</strong>
                    <span className="ml-2 text-muted">
                      {(childState.uniqueLinks || []).length} valid URLs loaded
                    </span>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.handleUniqueLinksReset}>
                    Replace file
                  </button>
                </div>
              )}
              {childState.uniqueLinksErrors && childState.uniqueLinksErrors.length > 0 ? (
                <div className="mt-2" style={{ color: '#c0392b', fontSize: 12 }}>
                  <strong>{childState.uniqueLinksErrors.length} issue(s):</strong>
                  <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                    {childState.uniqueLinksErrors.slice(0, 5).map((err: string, i: number) => (
                      <li key={i}>{err}</li>
                    ))}
                    {childState.uniqueLinksErrors.length > 5 ? (
                      <li>... and {childState.uniqueLinksErrors.length - 5} more</li>
                    ) : null}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="col">
              <label>{this.fieldLabel('Test Unique Links CSV', 'testUniqueLinks')}</label>
              {!childState.testUniqueLinksFile ? (
                <div>
                  <input
                    type="file"
                    accept=".csv,text/csv,.xlsx,.xls"
                    className="form-control-file"
                    onChange={this.handleTestUniqueLinksFile}
                  />
                  <small className="d-block text-muted mt-1">
                    Upload test links for this survey.
                  </small>
                </div>
              ) : (
                <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <strong>{childState.testUniqueLinksFile.name}</strong>
                    <span className="ml-2 text-muted">
                      {(childState.testUniqueLinks || []).length} valid URLs loaded
                    </span>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.handleTestUniqueLinksReset}>
                    Replace file
                  </button>
                </div>
              )}
              {childState.testUniqueLinksErrors && childState.testUniqueLinksErrors.length > 0 ? (
                <div className="mt-2" style={{ color: '#c0392b', fontSize: 12 }}>
                  <strong>{childState.testUniqueLinksErrors.length} issue(s):</strong>
                  <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                    {childState.testUniqueLinksErrors.slice(0, 5).map((err: string, i: number) => (
                      <li key={i}>{err}</li>
                    ))}
                    {childState.testUniqueLinksErrors.length > 5 ? (
                      <li>... and {childState.testUniqueLinksErrors.length - 5} more</li>
                    ) : null}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>

          {/* Ask 4 — current pool snapshot for this child. */}
          {(this.props.childId && this.state.poolStats) || (childState.uniqueLinks && childState.uniqueLinks.length > 0) || (childState.testUniqueLinks && childState.testUniqueLinks.length > 0) ? (
            <div className="row mt-2">
              <div className="col">
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <strong>Current pool:</strong>
                      <table style={{ marginTop: 4, borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={poolCellStyle}>Pool</th>
                            <th style={poolCellStyle}>Available</th>
                            <th style={poolCellStyle}>Claimed</th>
                            <th style={poolCellStyle}>Consumed</th>
                            <th style={poolCellStyle}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={poolCellStyle}><strong>Live</strong></td>
                            <td style={poolCellStyle}>{(this.state.poolStats?.stats?.available || 0) + (childState.uniqueLinks || []).length}</td>
                            <td style={poolCellStyle}>{this.state.poolStats?.stats?.claimed || 0}</td>
                            <td style={poolCellStyle}>{this.state.poolStats?.stats?.consumed || 0}</td>
                            <td style={poolCellStyle}>{(this.state.poolStats?.stats?.total || 0) + (childState.uniqueLinks || []).length}</td>
                          </tr>
                          <tr>
                            <td style={poolCellStyle}><strong>Test</strong></td>
                            <td style={poolCellStyle}>{(this.state.poolStats?.testStats?.available || 0) + (childState.testUniqueLinks || []).length}</td>
                            <td style={poolCellStyle}>{this.state.poolStats?.testStats?.claimed || 0}</td>
                            <td style={poolCellStyle}>{this.state.poolStats?.testStats?.consumed || 0}</td>
                            <td style={poolCellStyle}>{(this.state.poolStats?.testStats?.total || 0) + (childState.testUniqueLinks || []).length}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Ask 4 — post-save feedback. Form.tsx threads in the per-child
                  upload result from the most recent Save; cleared/overwritten
                  on each subsequent Save so it never lingers stale. */}
              {this.props.uniqueLinksUploadResult ? (
                <div className="row mt-2">
                  <div className="col">
                    <div style={{ marginTop: 4, color: (this.props.uniqueLinksUploadResult.error || (this.props.uniqueLinksUploadResult.added === 0 && this.props.uniqueLinksUploadResult.skipped_duplicates === 0)) ? 'red' : 'green', fontSize: 13 }}>
                      Upload result: {this.props.uniqueLinksUploadResult.added} added,{' '}
                      {this.props.uniqueLinksUploadResult.skipped_duplicates} duplicates skipped
                      {' '}(out of {this.props.uniqueLinksUploadResult.total_in_csv} in file)
                      {this.props.uniqueLinksUploadResult.error ? <div>Error: {this.props.uniqueLinksUploadResult.error}</div> : null}
                    </div>
                  </div>
                </div>
              ) : null}
          </>
        </Show>

        {/* Pre-screening: after the URLs for every market, panel and external. */}
        {preScreenSection}
      </div>
    );
  }
}
